/**
 * AttributionSlicesLane — Renders segment × overlay intersection slices
 *
 * Lane 5: Thin blocks showing which orders/drivers/fuel are active
 * in each movement segment intersection.
 */
import type { AttributionSlice, TimelineSelection, TimelineHover } from '../../../types/vehicleSceneTimeline';
import {
  timeWindowToRect,
  formatTime,
  formatDuration,
  formatConfidence,
  toMs,
  SEGMENT_COLORS,
  type TimeScale,
} from '../../../utils/timelineUtils';

interface AttributionSlicesLaneProps {
  slices: AttributionSlice[];
  scale: TimeScale;
  selection: TimelineSelection | null;
  hover: TimelineHover | null;
  onSelect: (sliceId: string) => void;
  onHover: (h: TimelineHover | null) => void;
}

const LANE_HEIGHT = 24;

export default function AttributionSlicesLane({
  slices,
  scale,
  selection,
  hover: _hover,
  onSelect,
  onHover,
}: AttributionSlicesLaneProps) {
  const isHighlighted = (sliceId: string): boolean => {
    if (!selection) return false;
    if (selection.type === 'slice' && selection.id === sliceId) return true;
    return selection.relatedSliceIds?.includes(sliceId) ?? false;
  };

  const isDimmed = (sliceId: string): boolean => {
    if (!selection) return false;
    return !isHighlighted(sliceId);
  };

  return (
    <div className="vst-lane" data-lane="attribution">
      <div className="vst-lane-label">
        <span className="vst-lane-dot" style={{ background: '#ec4899' }} />
        Attribution
      </div>
      <svg className="vst-lane-svg" width={scale.widthPx} height={LANE_HEIGHT}>
        {slices.map(slice => {
          const { x, width } = timeWindowToRect(slice.start_ts, slice.end_ts, scale);
          const highlighted = isHighlighted(slice.slice_id);
          const dimmed = isDimmed(slice.slice_id);
          const baseColor = SEGMENT_COLORS[slice.movement_state] || '#6b7280';

          return (
            <g
              key={slice.slice_id}
              className="vst-lane-item"
              pointerEvents="auto"
              onClick={() => onSelect(slice.slice_id)}
              onMouseEnter={e =>
                onHover({
                  type: 'slice',
                  id: slice.slice_id,
                  relatedSegmentIds: slice.movement_segment_ids,
                  relatedOrderIds: slice.active_order_ids,
                  relatedSliceIds: [slice.slice_id],
                  mouseX: e.clientX,
                  mouseY: e.clientY,
                })
              }
              onMouseLeave={() => onHover(null)}
              style={{ cursor: 'pointer' }}
            >
              <rect
                x={x}
                y={3}
                width={width}
                height={LANE_HEIGHT - 6}
                rx={2}
                fill={baseColor}
                opacity={dimmed ? 0.15 : 0.6}
                stroke={highlighted ? '#fff' : '#374151'}
                strokeWidth={highlighted ? 2 : 0.5}
              />
              {/* Confidence indicator bar at bottom */}
              <rect
                x={x}
                y={LANE_HEIGHT - 5}
                width={width * slice.confidence}
                height={2}
                fill="#ec4899"
                opacity={dimmed ? 0.3 : 0.9}
              />
              <title>
                {`Attribution Slice\n${formatTime(toMs(slice.start_ts))} – ${formatTime(toMs(slice.end_ts))} (${formatDuration(slice.start_ts, slice.end_ts)})\nMovement: ${slice.movement_state}\nOrders: ${slice.active_order_ids.length > 0 ? slice.active_order_ids.join(', ') : 'none'}\nDriver: ${slice.active_driver_id || 'none'}\nFuel: ${slice.active_fuel_ids.length > 0 ? slice.active_fuel_ids.join(', ') : 'none'}\nConfidence: ${formatConfidence(slice.confidence)}\nEvidence: ${slice.evidence_count} events\nEdges: ${slice.edge_ids.join(', ')}`}
              </title>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
